/**
 * Fit a multi-element device stack onto an ARBITRARY section (FXC-9009 CS fold).
 *
 * The default slotted-flap system (cove + vane + flaperon + deploy rig) is
 * authored on the LS(1)-0417 - a 17% thick foil - and the vane / hinge pivots /
 * flaperon nose tip are ABSOLUTE airfoil-frame coordinates. Riding those raw
 * numbers onto a different section (a C172's 9% wing section, say) parks the
 * vane half outside the skin and the hinges in free air - the "horribly
 * overlaid" composition this module exists to fix.
 *
 * The fit is anchored on the SLOT FRAME: the flaperon's nose-cut chord
 * (upper-cut point P0 -> lower-cut point P4), which is derivable on BOTH the
 * source and the target foil from the same chordwise cut fractions. Every
 * absolute-framed datum (vane control points, flaperon nose tip, hinge and
 * track pivots) is mapped through the similarity transform that carries the
 * source frame onto the target frame - translation, rotation, and uniform
 * scale, so the devices shrink/grow with the local slot and follow the target
 * camber. A clearance tuner then nudges (and if the channel is genuinely too
 * tight, shrinks) the vane until it holds a real gap to both the main's cove
 * and the flaperon nose across the STOWED pose.
 */

import type { AirfoilCoords, CoveCutouts, FlaperonShape, HighLiftAirfoil, HighLiftDesign, V2, VaneShape } from './estolConfig';
import { buildConfiguration, buildFlaperon, buildMainWing, buildVane, flaperonControlPoints } from './geometry';
import { motionGapCheck } from './motionGapCheck';

// ---------------------------------------------------------------------------
// Similarity transform between slot frames
// ---------------------------------------------------------------------------

interface FrameMap {
  sOrigin: V2;   // source frame origin (P4 on the source foil)
  tOrigin: V2;   // target frame origin (P4 on the target foil)
  sEx: V2;       // source frame unit axes
  sEy: V2;
  tEx: V2;       // target frame unit axes
  tEy: V2;
  scaleX: number;  // along the nose-cut chord (slot length)
  scaleY: number;  // across it (local THICKNESS ratio - a thin section gets a
                   // proportionally shallower device, which a pure similarity
                   // misses because the frame length is chord-dominated)
}

function frameOf(coords: AirfoilCoords, flaperon: FlaperonShape): { p4: V2; u: V2 } {
  const [p0, , , , p4] = flaperonControlPoints(coords, flaperon);
  return { p4, u: [p0[0] - p4[0], p0[1] - p4[1]] };
}

function interpY(pts: V2[], x: number): number {
  let best = pts[0] ?? [0, 0];
  for (let i = 0; i + 1 < pts.length; i++) {
    const a = pts[i]!;
    const b = pts[i + 1]!;
    if ((a[0] - x) * (b[0] - x) <= 0 && Math.abs(b[0] - a[0]) > 1e-12) {
      const t = (x - a[0]) / (b[0] - a[0]);
      return a[1] + t * (b[1] - a[1]);
    }
    if (Math.abs(a[0] - x) < Math.abs(best[0] - x)) best = a;
  }
  return best[1];
}

function thicknessAt(coords: AirfoilCoords, x: number): number {
  return Math.max(1e-6, interpY(coords.upper, x) - interpY(coords.lower, x));
}

function frameMapBetween(
  srcCoords: AirfoilCoords,
  tgtCoords: AirfoilCoords,
  flaperon: FlaperonShape,
): FrameMap {
  const s = frameOf(srcCoords, flaperon);
  const t = frameOf(tgtCoords, flaperon);
  const ls = Math.hypot(s.u[0], s.u[1]) || 1;
  const lt = Math.hypot(t.u[0], t.u[1]) || 1;
  const sEx: V2 = [s.u[0] / ls, s.u[1] / ls];
  const tEx: V2 = [t.u[0] / lt, t.u[1] / lt];
  // thickness measured where the device lives: the lower nose cut station
  const xRef = flaperon.lowerCutX;
  return {
    sOrigin: s.p4,
    tOrigin: t.p4,
    sEx,
    sEy: [-sEx[1], sEx[0]],
    tEx,
    tEy: [-tEx[1], tEx[0]],
    scaleX: lt / ls,
    scaleY: thicknessAt(tgtCoords, xRef) / thicknessAt(srcCoords, xRef),
  };
}

function mapPoint(m: FrameMap, p: V2): V2 {
  const dx = p[0] - m.sOrigin[0];
  const dy = p[1] - m.sOrigin[1];
  const a = dx * m.sEx[0] + dy * m.sEx[1];   // frame-local coordinates
  const b = dx * m.sEy[0] + dy * m.sEy[1];
  const ax = a * m.scaleX;
  const by = b * m.scaleY;
  return [
    m.tOrigin[0] + ax * m.tEx[0] + by * m.tEy[0],
    m.tOrigin[1] + ax * m.tEx[1] + by * m.tEy[1],
  ];
}

// ---------------------------------------------------------------------------
// Loop clearance utilities (polyline vs polyline)
// ---------------------------------------------------------------------------

function segPointDist2(p: V2, a: V2, b: V2): number {
  const abx = b[0] - a[0];
  const aby = b[1] - a[1];
  const l2 = abx * abx + aby * aby;
  let t = l2 > 0 ? ((p[0] - a[0]) * abx + (p[1] - a[1]) * aby) / l2 : 0;
  t = Math.max(0, Math.min(1, t));
  const dx = p[0] - (a[0] + t * abx);
  const dy = p[1] - (a[1] + t * aby);
  return dx * dx + dy * dy;
}

/** Min distance between two polylines + the closest points (a-side, b-side). */
export function loopClearance(a: V2[], b: V2[]): { dist: number; pa: V2; pb: V2 } {
  let best = Infinity;
  let pa: V2 = a[0] ?? [0, 0];
  let pb: V2 = b[0] ?? [0, 0];
  for (const p of a) {
    for (let j = 0; j + 1 < b.length; j++) {
      const d2 = segPointDist2(p, b[j]!, b[j + 1]!);
      if (d2 < best) { best = d2; pa = p; pb = closestOnSeg(p, b[j]!, b[j + 1]!); }
    }
  }
  for (const q of b) {
    for (let i = 0; i + 1 < a.length; i++) {
      const d2 = segPointDist2(q, a[i]!, a[i + 1]!);
      if (d2 < best) { best = d2; pb = q; pa = closestOnSeg(q, a[i]!, a[i + 1]!); }
    }
  }
  return { dist: Math.sqrt(best), pa, pb };
}

function closestOnSeg(p: V2, a: V2, b: V2): V2 {
  const abx = b[0] - a[0];
  const aby = b[1] - a[1];
  const l2 = abx * abx + aby * aby;
  let t = l2 > 0 ? ((p[0] - a[0]) * abx + (p[1] - a[1]) * aby) / l2 : 0;
  t = Math.max(0, Math.min(1, t));
  return [a[0] + t * abx, a[1] + t * aby];
}

function segsCross(a1: V2, a2: V2, b1: V2, b2: V2): boolean {
  const d = (a2[0] - a1[0]) * (b2[1] - b1[1]) - (a2[1] - a1[1]) * (b2[0] - b1[0]);
  if (Math.abs(d) < 1e-14) return false;
  const t = ((b1[0] - a1[0]) * (b2[1] - b1[1]) - (b1[1] - a1[1]) * (b2[0] - b1[0])) / d;
  const u = ((b1[0] - a1[0]) * (a2[1] - a1[1]) - (b1[1] - a1[1]) * (a2[0] - a1[0])) / d;
  return t > 0 && t < 1 && u > 0 && u < 1;
}

/** True when two closed polylines intersect (any segment pair crosses). */
export function loopsIntersect(a: V2[], b: V2[]): boolean {
  for (let i = 0; i + 1 < a.length; i++) {
    for (let j = 0; j + 1 < b.length; j++) {
      if (segsCross(a[i]!, a[i + 1]!, b[j]!, b[j + 1]!)) return true;
    }
  }
  return false;
}

/** Even-odd point-in-polygon (closed loop; robust enough for chord-frame contours). */
export function pointInLoop(p: V2, loop: V2[]): boolean {
  let inside = false;
  for (let i = 0, j = loop.length - 1; i < loop.length; j = i++) {
    const a = loop[i]!;
    const b = loop[j]!;
    if (a[1] > p[1] !== b[1] > p[1]
        && p[0] < ((b[0] - a[0]) * (p[1] - a[1])) / (b[1] - a[1]) + a[0]) {
      inside = !inside;
    }
  }
  return inside;
}

/**
 * WHAT AN OVERLAP IS, made precise: two element contours overlap when either
 * loop's boundary crosses the other's, or one loop's vertices lie strictly
 * INSIDE the other (full containment never crosses an edge). Its magnitude is
 * the PENETRATION DEPTH: the deepest any vertex of one loop sits inside the
 * other, measured as its distance back to that loop's boundary - i.e. how far
 * the elements must move apart to merely TOUCH. The escape vector (deepest
 * inside point -> its nearest boundary point) is the direction that reduces
 * it fastest, which is exactly what the fit tuner follows.
 *
 * Returns depth 0 for disjoint loops. `at`/`toward` are the deepest inside
 * vertex and its nearest boundary point (present only when depth > 0).
 */
export function penetrationDepth(
  a: V2[],
  b: V2[],
): { depth: number; at?: V2; toward?: V2 } {
  let depth = 0;
  let at: V2 | undefined;
  let toward: V2 | undefined;
  const probe = (pts: V2[], loop: V2[]): void => {
    // vertices AND edge midpoints: an axis-aligned overlap can trap an edge
    // interior while every vertex sits exactly ON the other boundary
    const samples: V2[] = pts.slice();
    for (let i = 0; i + 1 < pts.length; i++) {
      samples.push([(pts[i]![0] + pts[i + 1]![0]) / 2, (pts[i]![1] + pts[i + 1]![1]) / 2]);
    }
    for (const p of samples) {
      if (!pointInLoop(p, loop)) continue;
      // distance from the trapped vertex back OUT to the containing boundary
      let best = Infinity;
      let bp: V2 = p;
      for (let j = 0; j < loop.length; j++) {
        const s = loop[j]!;
        const e = loop[(j + 1) % loop.length]!;
        const d2 = segPointDist2(p, s, e);
        if (d2 < best) { best = d2; bp = closestOnSeg(p, s, e); }
      }
      const d = Math.sqrt(best);
      if (d > depth) { depth = d; at = p; toward = bp; }
    }
  };
  probe(a, b);
  probe(b, a);
  return at && toward ? { depth, at, toward } : { depth: 0 };
}

/**
 * SIGNED clearance between two contours: positive = the minimum gap between
 * disjoint loops; negative = minus the penetration depth of an overlap. The
 * single number the tuners maximise and the pre-mesh gate thresholds on.
 */
export function signedClearance(a: V2[], b: V2[]): number {
  const pen = penetrationDepth(a, b);
  if (pen.depth > 0) return -pen.depth;
  if (loopsIntersect(a, b)) return 0;  // edges graze without a trapped vertex
  return loopClearance(a, b).dist;
}

// ---------------------------------------------------------------------------
// The pre-mesh boundary gate
// ---------------------------------------------------------------------------

export interface OverlapViolation {
  a: string;
  b: string;
  /** Penetration depth in chords (>0) or 0 for an edge-graze. */
  penetrationC: number;
}

/**
 * The HARD GATE every mesh submission must pass: no pair of element contours
 * may overlap at the pose being meshed. Meshing overlapping contours does not
 * fail loudly - it hands the mesher an impossible boundary and burns minutes
 * before a cryptic CDT error comes back (measured: warm-solve exit 2 on the
 * un-gated overlaid composition). Run this on the EXACT configuration going
 * to the mesher and refuse on any violation.
 */
export function meshBoundaryCheck(cfg: HighLiftAirfoil): {
  ok: boolean;
  violations: OverlapViolation[];
} {
  const conf = buildConfiguration(cfg);
  const loops = conf.elements.map((e) => ({ name: e.label || e.kind, loop: e.contour }));
  const violations: OverlapViolation[] = [];
  for (let i = 0; i < loops.length; i++) {
    for (let j = i + 1; j < loops.length; j++) {
      const sc = signedClearance(loops[i]!.loop, loops[j]!.loop);
      if (sc <= 0) {
        violations.push({
          a: loops[i]!.name,
          b: loops[j]!.name,
          penetrationC: -sc,
        });
      }
    }
  }
  return { ok: violations.length === 0, violations };
}

// ---------------------------------------------------------------------------
// The fit
// ---------------------------------------------------------------------------

export interface FittedSlottedSystem {
  cove: CoveCutouts;
  vane: VaneShape;
  flaperon: FlaperonShape;
  deployHinge: HighLiftDesign['deployHinge'];
  flaperonHinge: HighLiftDesign['flaperonHinge'];
  deployTrack?: HighLiftDesign['deployTrack'];
  motion?: HighLiftDesign['motion'];
  vaneFollower?: HighLiftDesign['vaneFollower'];
}

/** Clearance targets, in chord units (the section frame is chord-normalized). */
const CLEAR_MIN_CAP = 0.005;    // never ask for more than 0.5%c...
const CLEAR_MIN_FLOOR = 0.002;  // ...or less than 0.2%c
const CLEAR_FRACTION = 0.12;    // of the local section thickness at the flap cut
const CLEAR_STEP_CAP = 0.02;
const TUNE_ITERS = 28;

/** The clearance target adapts to the LOCAL channel: a thin section cannot
 *  hold the fat-foil gap on both sides of a usefully-sized vane. */
function clearMinFor(coords: AirfoilCoords, flaperon: FlaperonShape): number {
  const t = thicknessAt(coords, flaperon.lowerCutX);
  return Math.min(CLEAR_MIN_CAP, Math.max(CLEAR_MIN_FLOOR, CLEAR_FRACTION * t));
}

/** The flaperon deflection poses the fit must stay overlap-free through -
 *  the registry's default flaperon range (deg, TE-down positive). Overlap at
 *  a DEFLECTED pose is exactly the reported failure: the stowed fit looked
 *  fine while the 20-deg flaperon nose swung up into the cove ceiling. */
const FLAPERON_POSES_DEG: readonly number[] = [0];

function rotateLoop(loop: V2[], deg: number, pivot: V2): V2[] {
  if (deg === 0) return loop;
  // TE-down positive = clockwise in the x-aft/y-up frame (matches geometry.ts)
  const r = (-deg * Math.PI) / 180;
  const c = Math.cos(r);
  const sn = Math.sin(r);
  return loop.map((p) => {
    const dx = p[0] - pivot[0];
    const dy = p[1] - pivot[1];
    return [pivot[0] + c * dx - sn * dy, pivot[1] + sn * dx + c * dy] as V2;
  });
}

/** The flaperon contour at each deflection pose about its hinge. */
function posedFlapLoops(
  coords: AirfoilCoords,
  flaperon: FlaperonShape,
  hinge: V2,
  bluntThickness: number,
): V2[][] {
  const stowed = buildFlaperon(coords, flaperon, bluntThickness);
  return FLAPERON_POSES_DEG.map((deg) => rotateLoop(stowed, deg, hinge));
}

/**
 * Map the source design's slotted-flap system onto `tgtCoords` and tune the
 * vane's clearance. Pure - returns fresh objects, mutates nothing.
 *
 * `withVane: false` fits the SINGLE-SLOTTED flap: cove + flaperon + rig only
 * (the slot forms directly between the cove exit and the flap nose).
 */
export function fitSlottedSystemToSection(
  tgtCoords: AirfoilCoords,
  source: HighLiftDesign,
  srcCoords: AirfoilCoords,
  bluntThickness: number,
  opts?: { withVane?: boolean },
): FittedSlottedSystem {
  const withVane = opts?.withVane !== false;
  const flaperon: FlaperonShape = structuredClone(source.flaperon);
  const m = frameMapBetween(srcCoords, tgtCoords, flaperon);

  // The flaperon's cuts + tangents are chordwise (they re-derive on the target
  // foil); only its nose tip is absolute - map it into the target slot frame.
  flaperon.noseTip = mapPoint(m, flaperon.noseTip);

  const cove: CoveCutouts = structuredClone(source.cove);

  const deployHinge = { ...structuredClone(source.deployHinge), pivot: mapPoint(m, source.deployHinge.pivot) };
  const flaperonHinge = { ...structuredClone(source.flaperonHinge), pivot: mapPoint(m, source.flaperonHinge.pivot) };
  const deployTrack = source.deployTrack
    ? {
        pivot: mapPoint(m, source.deployTrack.pivot),
        waypoints: source.deployTrack.waypoints.map((w) => mapPoint(m, w)),
      }
    : undefined;

  const fitted: FittedSlottedSystem = {
    cove,
    vane: structuredClone(source.vane),
    flaperon: tuneFlaperonNose(tgtCoords, cove, flaperon, flaperonHinge.pivot, bluntThickness),
    deployHinge,
    flaperonHinge,
    ...(deployTrack ? { deployTrack } : {}),
    ...(source.motion ? { motion: structuredClone(source.motion) } : {}),
    ...(source.vaneFollower ? { vaneFollower: structuredClone(source.vaneFollower) } : {}),
  };

  if (!withVane) return fitted;

  // Vane: every control point is absolute - map the whole spline cage.
  const vane: VaneShape = {
    te: mapPoint(m, source.vane.te),
    points: source.vane.points.map((p) => mapPoint(m, p)),
  };
  fitted.vane = tuneVaneClearance(tgtCoords, cove, vane, fitted.flaperon, flaperonHinge.pivot, bluntThickness);

  // OUTER loop: the stowed tune can still collide somewhere in the MOTION
  // ENVELOPE (the reported failure: a clean stowed fit whose 20-deg flaperon
  // nose swings into the cove). Validate with the app's OWN range-of-motion
  // metric - the same sweep that drives the collision chip - and, while it
  // reports a collision, retract the flaperon nose and contract the vane a
  // notch. A fit that is already envelope-clean takes zero rounds (identity).
  for (let round = 0; round < 4; round++) {
    if (!fittedEnvelopeCollides(tgtCoords, fitted, bluntThickness)) break;
    fitted.flaperon = {
      ...fitted.flaperon,
      noseTip: retractToward(tgtCoords, fitted.flaperon, 0.85),
      noseTangentUpper: fitted.flaperon.noseTangentUpper * 0.88,
      noseTangentLower: fitted.flaperon.noseTangentLower * 0.88,
    };
    fitted.vane = contractVane(fitted.vane, 0.92);
    fitted.vane = tuneVaneClearance(
      tgtCoords, cove, fitted.vane, fitted.flaperon, flaperonHinge.pivot, bluntThickness,
    );
  }
  return fitted;
}

function retractToward(coords: AirfoilCoords, flaperon: FlaperonShape, k: number): V2 {
  const [p0, , , , p4] = flaperonControlPoints(coords, flaperon);
  const mid: V2 = [(p0[0] + p4[0]) / 2, (p0[1] + p4[1]) / 2];
  return [
    mid[0] + k * (flaperon.noseTip[0] - mid[0]),
    mid[1] + k * (flaperon.noseTip[1] - mid[1]),
  ];
}

function contractVane(vane: VaneShape, k: number): VaneShape {
  const all = [vane.te, ...vane.points];
  const cx = all.reduce((a, p) => a + p[0], 0) / all.length;
  const cy = all.reduce((a, p) => a + p[1], 0) / all.length;
  const sc = (p: V2): V2 => [cx + k * (p[0] - cx), cy + k * (p[1] - cy)];
  return { te: sc(vane.te), points: vane.points.map(sc) };
}

/** Range-of-motion collision per the app's own sweep ({@link motionGapCheck}). */
function fittedEnvelopeCollides(
  coords: AirfoilCoords,
  fitted: FittedSlottedSystem,
  bluntThickness: number,
): boolean {
  const design: HighLiftDesign = {
    cove: fitted.cove,
    vane: fitted.vane,
    flaperon: fitted.flaperon,
    deployHinge: fitted.deployHinge,
    flaperonHinge: fitted.flaperonHinge,
    ...(fitted.deployTrack ? { deployTrack: fitted.deployTrack } : {}),
    ...(fitted.motion ? { motion: fitted.motion } : {}),
    ...(fitted.vaneFollower ? { vaneFollower: fitted.vaneFollower } : {}),
    elements: [
      { kind: 'main-cove', id: 'main', label: 'Main', cove: fitted.cove },
      { kind: 'vane-bspline', id: 'vane', label: 'Vane', shape: fitted.vane },
      { kind: 'flaperon-derived', id: 'flaperon', label: 'Flaperon', shape: fitted.flaperon },
    ],
    blowing: [],
  };
  const probe: HighLiftAirfoil = {
    main: { coords: structuredClone(coords), bluntThickness, chord: 1 },
    design,
    propeller: { x: -0.1, z: -0.2, diameter: 0.75, tiltDeg: 10 },
    operation: {
      deployAngleDeg: 0, flaperonAngleDeg: 0, spoilerAngleDeg: 0, diskLoading: 0,
      freestreamKnots: 100, densityAltitudeM: 0, tempOffsetK: 0,
    },
  };
  return motionGapCheck(probe, { deploySteps: 5, flaperonSteps: 3 }).status === 'collision';
}

/**
 * Pull the flaperon's nose back until it clears the main's carved cove in the
 * stowed pose. The cove walls on the TARGET foil are re-derived from the cove
 * cut fractions, so a nose spline mapped from a thicker source foil can still
 * pierce them; the tuner retracts `noseTip` toward the nose-cut chord midpoint
 * and relaxes the cut tangents until the loops neither cross nor crowd below
 * {@link CLEAR_MIN}. A fit that is already clean is returned untouched (the
 * source-onto-itself identity).
 */
export function tuneFlaperonNose(
  coords: AirfoilCoords,
  cove: CoveCutouts,
  flaperon: FlaperonShape,
  hinge: V2,
  bluntThickness: number,
): FlaperonShape {
  const mainLoop = buildMainWing(coords, cove, bluntThickness, 0, 1);
  const clearMin = clearMinFor(coords, flaperon);
  let cur: FlaperonShape = structuredClone(flaperon);
  for (let it = 0; it < TUNE_ITERS; it++) {
    // the nose must clear the cove in EVERY deflection pose, not just stowed
    const worst = Math.min(
      ...posedFlapLoops(coords, cur, hinge, bluntThickness)
        .map((loop) => signedClearance(loop, mainLoop)),
    );
    if (worst >= clearMin) break;
    const [p0, , , , p4] = flaperonControlPoints(coords, cur);
    const mid: V2 = [(p0[0] + p4[0]) / 2, (p0[1] + p4[1]) / 2];
    cur = {
      ...cur,
      noseTip: [
        mid[0] + 0.82 * (cur.noseTip[0] - mid[0]),
        mid[1] + 0.82 * (cur.noseTip[1] - mid[1]),
      ],
      noseTangentUpper: cur.noseTangentUpper * 0.85,
      noseTangentLower: cur.noseTangentLower * 0.85,
    };
  }
  return cur;
}

/**
 * Nudge (and as a last resort shrink) the vane until it clears both the
 * main's cove and the flaperon nose by {@link CLEAR_MIN} in the stowed pose.
 * The move direction is always along the line of closest approach - away
 * from whichever neighbour it crowds - so the tuner follows the real channel
 * shape rather than an assumed axis. If both sides are too tight the channel
 * is narrower than the vane: contract it about its centroid and retry.
 */
export function tuneVaneClearance(
  coords: AirfoilCoords,
  cove: CoveCutouts,
  vane: VaneShape,
  flaperon: FlaperonShape,
  hinge: V2,
  bluntThickness: number,
): VaneShape {
  const mainLoop = buildMainWing(coords, cove, bluntThickness, 0, 1);
  // the vane must clear the flaperon through its WHOLE deflection range
  const flapLoops = posedFlapLoops(coords, flaperon, hinge, bluntThickness);
  const clearMin = clearMinFor(coords, flaperon);
  let cur: VaneShape = structuredClone(vane);
  let best: VaneShape = cur;
  let bestScore = -Infinity;

  const shift = (s: VaneShape, d: V2): VaneShape => ({
    te: [s.te[0] + d[0], s.te[1] + d[1]],
    points: s.points.map((p) => [p[0] + d[0], p[1] + d[1]] as V2),
  });
  const contract = (s: VaneShape, k: number): VaneShape => {
    const all = [s.te, ...s.points];
    const cx = all.reduce((a, p) => a + p[0], 0) / all.length;
    const cy = all.reduce((a, p) => a + p[1], 0) / all.length;
    const sc = (p: V2): V2 => [cx + k * (p[0] - cx), cy + k * (p[1] - cy)];
    return { te: sc(s.te), points: s.points.map(sc) };
  };

  let lastSide: 'main' | 'flap' | null = null;
  for (let it = 0; it < TUNE_ITERS; it++) {
    const loop = buildVane(cur, bluntThickness);
    const scM = signedClearance(loop, mainLoop);
    const scF = Math.min(...flapLoops.map((fl) => signedClearance(loop, fl)));
    const worstFlap = flapLoops.reduce(
      (acc, fl) => (signedClearance(loop, fl) === scF ? fl : acc), flapLoops[0]!,
    );
    const tightM = scM < clearMin;
    const tightF = scF < clearMin;

    const score = Math.min(scM, scF);
    if (score > bestScore) { bestScore = score; best = cur; }

    if (!tightM && !tightF) { best = cur; break; }

    const side: 'main' | 'flap' = scM <= scF ? 'main' : 'flap';
    if ((tightM && tightF) || (lastSide !== null && lastSide !== side)) {
      // both sides tight, or ping-ponging between them: the channel is
      // narrower than the vane - contract about the centroid and re-measure
      cur = contract(cur, 0.88);
      lastSide = null;
      continue;
    }
    lastSide = side;
    const other = side === 'main' ? mainLoop : worstFlap;
    const sc = side === 'main' ? scM : scF;
    let dx: number;
    let dy: number;
    if (sc < 0) {
      // penetrating: escape along the penetration vector - the deepest
      // trapped vertex back to its nearest boundary point is the direction
      // that reduces the overlap fastest
      const pen = penetrationDepth(loop, other);
      if (pen.at && pen.toward) {
        dx = pen.toward[0] - pen.at[0];
        dy = pen.toward[1] - pen.at[1];
      } else {
        const c = loopClearance(loop, other);
        dx = c.pa[0] - c.pb[0];
        dy = c.pa[1] - c.pb[1];
      }
    } else {
      // clear but too close: move straight down the line of closest approach
      const c = loopClearance(loop, other);
      dx = c.pa[0] - c.pb[0];
      dy = c.pa[1] - c.pb[1];
    }
    const len = Math.hypot(dx, dy) || 1;
    dx /= len; dy /= len;
    const need = clearMin - sc;
    const step = Math.min(CLEAR_STEP_CAP, need + 0.001);
    cur = shift(cur, [dx * step, dy * step]);
  }
  return best;
}
